import { useEffect, useState } from 'react';

/**
 * Хук для отложенного обновления значения (debounce)
 * 
 * @template T - Тип значения для debounce
 * @param {T} value - Значение, которое нужно отложить
 * @param {number} delay - Задержка в миллисекундах перед обновлением значения
 * @returns {T} Отложенное значение
 * 
 * @example
 * ```tsx
 * const [searchQuery, setSearchQuery] = useState('');
 * const debouncedQuery = useDebounce(searchQuery, 300);
 * 
 * // debouncedQuery обновится только через 300ms после последнего изменения searchQuery
 * useEffect(() => {
 *   if (debouncedQuery) {
 *     // Выполнить поиск
 *   }
 * }, [debouncedQuery]);
 * ```
 */
export const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Устанавливаем таймер для обновления отложенного значения
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Очищаем таймер при размонтировании или изменении value/delay
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};
